import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { TableModule } from "primeng/table";
import { TabsModule } from "primeng/tabs";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { finalize } from "rxjs";
import { ProofApprovalDto } from "../../../core/models/proof-approval/proof-approval.dto";
import { SendSelectedProofApprovalsResultDto } from "../../../core/models/proof-approval/send-selected-proof-approvals-result.dto";
import { ProofApprovalService } from "../../../core/services/proof-approval.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { SignalRService } from "../../../core/services/signal-r.service";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";

/** Maximum rows requested per list call; mirrors the Nadel Portal API's server-side page size cap. */
const MAX_PAGE_SIZE = 200;

@Component({
  selector: "jni-proof-email-queue",
  templateUrl: "./proof-email-queue.component.html",
  styleUrls: ["./proof-email-queue.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    OverlayBadgeModule,
    TableModule,
    TabsModule,
    TagModule,
    TooltipModule,
    SpinnerComponent,
  ],
})
export class ProofEmailQueueComponent {
  /** Project workflow whose proof-uploaded notifications are shown and sent from this modal. */
  readonly projectWorkflowId = input.required<number>();

  readonly visible = signal(false);
  readonly activeTab = signal("queue");
  readonly selectedRecords = signal<ProofApprovalDto[]>([]);
  readonly queue = signal<ProofApprovalDto[]>([]);
  readonly history = signal<ProofApprovalDto[]>([]);
  readonly loading = signal(false);
  readonly sending = signal(false);

  private readonly service = inject(ProofApprovalService);
  private readonly toastr = inject(JniToastrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly signalR = inject(SignalRService);

  constructor() {
    effect(() => {
      const workflowId = this.projectWorkflowId();
      if (workflowId > 0) {
        this.loadData();
      }
    });

    effect(() => {
      const notification = this.signalR.notifications();
      if (
        notification !== false &&
        notification.isAllowed &&
        notification.moduleName === "ProductionWorkflow"
      ) {
        this.loadData();
      }
    });
  }

  get pendingCount(): number {
    return this.queue().length;
  }

  open(): void {
    if (this.visible()) {
      return;
    }

    this.visible.set(true);
    this.loadData();
  }

  close(): void {
    if (!this.visible()) {
      return;
    }

    this.visible.set(false);
    this.selectedRecords.set([]);
    this.activeTab.set("queue");
  }

  refresh(): void {
    this.selectedRecords.set([]);
    this.loadData();
  }

  sendEmail(records: ProofApprovalDto[]): void {
    if (!records.length || this.sending()) {
      return;
    }

    const ids = records.flatMap(r => r.ids ?? [r.id]);
    this.sending.set(true);
    this.service
      .sendSelectedProofApprovals(ids)
      .pipe(
        finalize(() => this.sending.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          this.reportSendResult(result, ids.length);
          this.refresh();
        },
        error: (error: unknown) => {
          this.toastr.error(
            "Unable to send notifications",
            error instanceof Error
              ? error.message
              : "Please refresh and try again."
          );
          this.refresh();
        },
      });
  }

  private reportSendResult(
    result: SendSelectedProofApprovalsResultDto,
    requestedCount: number
  ): void {
    const sentCount = result.sentIds.length;
    const failedCount = result.failedRecipients.length;
    const skippedCount = result.skippedIds.length;

    if (sentCount === requestedCount) {
      this.toastr.success(
        sentCount === 1
          ? "Email sent successfully."
          : `Emails sent successfully.`
      );
      return;
    }

    if (sentCount > 0) {
      this.toastr.warning(
        `${sentCount} of ${requestedCount} email(s) sent. ` +
          (failedCount > 0
            ? `${failedCount} recipient(s) failed to send. `
            : "") +
          (skippedCount > 0
            ? `${skippedCount} notification(s) were already handled.`
            : ""),
        "Partially sent"
      );
      return;
    }

    this.toastr.error(
      failedCount > 0
        ? result.failedRecipients
            .map(f => `${f.email}: ${f.errorMessage}`)
            .join(" ")
        : "None of the selected notifications could be sent.",
      "Unable to send notifications"
    );
  }

  private loadData(): void {
    this.loading.set(true);
    this.service
      .listProofApprovals(
        0,
        MAX_PAGE_SIZE,
        [
          {
            columnName: "PROJECTWORKFLOWID",
            value: String(this.projectWorkflowId()),
            matchMode: "equals",
            tableName: "",
          },
        ],
        [{ field: "Id", order: 1 }]
      )
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const rows = response.data ?? [];

          const isPending = (row: ProofApprovalDto) =>
            row.status === "Pending" || (!row.sentAt && row.status !== "Sent");

          const isHistory = (row: ProofApprovalDto) =>
            row.status === "Sent" || !!row.sentAt || row.status !== "Pending";

          this.queue.set(this.service.groupByRecipient(rows.filter(isPending)));
          this.history.set(
            this.service.groupByRecipient(rows.filter(isHistory))
          );
        },
        error: () => {
          this.queue.set([]);
          this.history.set([]);
          this.toastr.error(
            "Unable to load proof notifications",
            "Please refresh and try again."
          );
        },
      });
  }
}
